import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  createRef,
  CSSProperties,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../helpers/src/interaction/interactionHelpers';
import {EventHandler, HandlerOf, KeyboardEventHandler} from '../../../../helpers/src/react/reactHelpers';
import {greys} from '../../styles/colorStyles';
import {appearanceNone, textSelection} from '../../styles/mixins';

/*
 * Props.
 */

export interface TextAreaInheritableProps {
  /** The text to display when the TextArea is empty. */
  placeholder?: string;
  /** Set the initial value of the TextArea. */
  value?: string;
  /** Abstracted event handler providing the updated text. */
  onChange?: HandlerOf<string>;
  /** Event handler called for every keydown event. */
  onKeydown?: KeyboardEventHandler;
  /** Event handler called when the TextArea receives focus. */
  onFocus?: EventHandler<HTMLTextAreaElement>;
  /** Event handler called when the TextArea is blurred. */
  onBlur?: EventHandler<HTMLTextAreaElement>;
  /** Abstracted event handler providing the updated text. */
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
  /** Whether the TextArea should focus after mount. */
  shouldFocus?: boolean;
  /** Specifies the maximum length in characters of TextArea */
  maxLength?: number;
  /** Defines if TextArea is resizable by the user */
  shouldAllowResize?: boolean;
}

export interface TextAreaProps extends TextAreaInheritableProps, InteractionComponentProps {
  textAreaId?: string;
  className?: string;
  style?: CSSProperties;
  isDisabled?: boolean;
  hasSpellCheck?: boolean;
}

type TextAreaHandle = {
  focus: (shouldSelectAll?: boolean, shouldResizeTextArea?: boolean) => void;
};

/*
 * Style.
 */

interface NewTextAreaStyleProps {
  $shouldAllowResize: boolean;
}
const StyledTextArea = styled.textarea<NewTextAreaStyleProps>`
  display: block;
  color: inherit;
  background: transparent;
  outline: 0;
  border: 0;
  padding: 0;
  resize: ${(p) => (p.$shouldAllowResize ? 'both' : 'none')};

  ${appearanceNone()};
  ${textSelection()};

  ::placeholder {
    color: ${greys.shade50};
  }
`;

/*
 * Component.
 */

export const TextArea = forwardRef<TextAreaHandle, TextAreaProps>((props, ref) => {
  const textAreaRef = createRef<HTMLTextAreaElement>();

  useImperativeHandle(ref, () => ({
    focus(shouldSelectAll?: boolean, shouldResizeTextArea?: boolean) {
      if (!textAreaRef.current) {
        return;
      }

      // Check if we should select all the contents of the area or just move the cursor to the end
      const selectionStart = shouldSelectAll ? 0 : props?.value?.length ?? 0;
      textAreaRef.current.setSelectionRange(selectionStart, props?.value?.length ?? 0);

      if (shouldResizeTextArea) {
        // To auto scale the size of the note, we need to set the height of this property.
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      }
    },
  }));

  const onChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    // If we don't have a change handler, nothing to do.
    if (!props.onChange) {
      return;
    }

    const value = event.currentTarget.value || '';
    props.onChange(value);
  };

  return (
    <StyledTextArea
      id={props.textAreaId}
      ref={textAreaRef}
      className={props.className}
      style={props.style}
      disabled={props.isDisabled}
      value={props.value}
      placeholder={props.placeholder}
      onChange={onChange}
      onKeyDown={props.onKeydown}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onPaste={props.onPaste}
      autoFocus={props.shouldFocus}
      autoComplete="off"
      spellCheck={props.hasSpellCheck}
      maxLength={props.maxLength}
      $shouldAllowResize={Boolean(props.shouldAllowResize)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps(props.interactionId)}
    />
  );
});
